import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  EyeIcon,
  EyeSlashIcon,
  ClipboardIcon,
  CheckIcon,
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import Card from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuthStore } from '../stores/authStore';
import { useGuardianAuthStore } from '../stores/guardianAuthStore';
import { DEV_CREDENTIALS, type DevCredential } from '../data/dev/credentials';

const roleBadgeClass: Record<DevCredential['role'], string> = {
  super_admin: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-800',
  principal: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-200 dark:border-purple-800',
  teacher: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800',
  guardian: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800',
};

const roleLabel: Record<DevCredential['role'], string> = {
  super_admin: 'Super Admin',
  principal: 'Principal',
  teacher: 'Teacher',
  guardian: 'Guardian',
};

/**
 * Dev-only credentials reference page.
 *
 * Renders a card per seeded test user with one-click "Login as" buttons
 * that drive the appropriate auth store (staff or guardian) and route
 * to the right landing page. Hidden behind `import.meta.env.DEV` at the
 * route level so it never ships to a production build.
 *
 * Why this exists: the seeded passwords ("magic123", "protea123",
 * "platform-admin-2026") get stale in our heads as new contributors
 * come and go. A single discoverable page beats hunting through seed
 * scripts every time someone wants to try a different role.
 */
const DevCredentials: React.FC = () => {
  const navigate = useNavigate();
  const staffLogin = useAuthStore((s) => s.login);
  const guardianLogin = useGuardianAuthStore((s) => s.login);

  const [revealedPasswords, setRevealedPasswords] = useState<Set<string>>(new Set());
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [loginInFlight, setLoginInFlight] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);

  const togglePassword = (username: string) => {
    setRevealedPasswords((prev) => {
      const next = new Set(prev);
      if (next.has(username)) next.delete(username);
      else next.add(username);
      return next;
    });
  };

  const copyToClipboard = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedItem(key);
      setTimeout(() => setCopiedItem((c) => (c === key ? null : c)), 1500);
    } catch {
      // Clipboard API can fail on insecure contexts -- silently ignore
      // since this is a dev-only convenience.
    }
  };

  const handleLogin = async (cred: DevCredential) => {
    setLoginError(null);
    setLoginInFlight(cred.username);
    try {
      if (cred.loginPath === 'guardian') {
        // Protea guardians auto-resolve their org via the username, but
        // pass the code explicitly when we know it for faster lookup.
        await guardianLogin(cred.username, cred.password, 'PROTEA');
        navigate('/guardian/dashboard');
      } else {
        await staffLogin({ username: cred.username, password: cred.password });
        navigate('/dashboard');
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Login failed';
      setLoginError(`Login failed for ${cred.username}: ${message}`);
    } finally {
      setLoginInFlight(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="dev-credentials-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <header className="mb-6">
          <h1 className="text-2xl font-display font-bold text-gray-900 dark:text-gray-100">
            Dev — Test credentials
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Seeded users for local development. Click <em>Login as</em> to
            switch identities; passwords are visible behind the eye icon.
          </p>
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm">
            <ExclamationTriangleIcon
              className="w-4 h-4 text-amber-600 dark:text-amber-300 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div className="text-amber-900 dark:text-amber-100">
              <strong>Dev only.</strong> This page is gated by{' '}
              <code className="text-xs">import.meta.env.DEV</code>. Make sure
              the seeded passwords listed here never ship to production.
            </div>
          </div>
        </header>

        {loginError && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-3 text-sm text-rose-900 dark:text-rose-100"
          >
            {loginError}
          </div>
        )}

        <div className="space-y-8">
          {DEV_CREDENTIALS.map((group) => (
            <section key={group.title}>
              <div className="mb-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {group.title}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {group.description}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.credentials.map((cred) => (
                  <Card key={cred.username} className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {cred.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {cred.context}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span
                          className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold rounded border ${roleBadgeClass[cred.role]}`}
                        >
                          {roleLabel[cred.role]}
                        </span>
                        {cred.children && cred.children.length > 1 && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold rounded border bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-200 dark:border-purple-800"
                            title={`${cred.children.length} children: ${cred.children.join(', ')}`}
                            data-testid={`multi-child-badge-${cred.username}`}
                          >
                            <UsersIcon className="w-3 h-3" aria-hidden="true" />
                            {cred.children.length} children
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Username + copy */}
                    <CredentialField
                      label="Username"
                      value={cred.username}
                      copyKey={`${cred.username}-username`}
                      copiedItem={copiedItem}
                      onCopy={copyToClipboard}
                    />

                    {/* Password — visibility toggle then copy */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">
                          Password
                        </span>
                        <button
                          type="button"
                          onClick={() => togglePassword(cred.username)}
                          className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          data-testid={`toggle-pw-${cred.username}`}
                          aria-label={
                            revealedPasswords.has(cred.username)
                              ? 'Hide password'
                              : 'Show password'
                          }
                        >
                          {revealedPasswords.has(cred.username) ? (
                            <EyeSlashIcon className="w-4 h-4" aria-hidden="true" />
                          ) : (
                            <EyeIcon className="w-4 h-4" aria-hidden="true" />
                          )}
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 px-2 py-1 text-xs font-mono bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 truncate">
                          {revealedPasswords.has(cred.username)
                            ? cred.password
                            : '•'.repeat(Math.min(cred.password.length, 16))}
                        </code>
                        <button
                          type="button"
                          onClick={() =>
                            copyToClipboard(`${cred.username}-pw`, cred.password)
                          }
                          className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800"
                          aria-label="Copy password"
                        >
                          {copiedItem === `${cred.username}-pw` ? (
                            <CheckIcon
                              className="w-4 h-4 text-emerald-600"
                              aria-hidden="true"
                            />
                          ) : (
                            <ClipboardIcon className="w-4 h-4" aria-hidden="true" />
                          )}
                        </button>
                      </div>
                    </div>

                    {cred.hint && (
                      <p className="text-xs text-gray-600 dark:text-gray-300 italic">
                        {cred.hint}
                      </p>
                    )}

                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleLogin(cred)}
                      disabled={loginInFlight !== null}
                      data-testid={`login-as-${cred.username}`}
                      className="w-full"
                    >
                      <ArrowRightOnRectangleIcon
                        className="w-4 h-4 mr-1.5"
                        aria-hidden="true"
                      />
                      {loginInFlight === cred.username
                        ? 'Logging in…'
                        : `Login as ${cred.name.split(' ')[0]}`}
                    </Button>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

interface CredentialFieldProps {
  label: string;
  value: string;
  copyKey: string;
  copiedItem: string | null;
  onCopy: (key: string, value: string) => void;
}

const CredentialField: React.FC<CredentialFieldProps> = ({
  label,
  value,
  copyKey,
  copiedItem,
  onCopy,
}) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400 mb-1">
      {label}
    </div>
    <div className="flex items-center gap-2">
      <code className="flex-1 px-2 py-1 text-xs font-mono bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 truncate">
        {value}
      </code>
      <button
        type="button"
        onClick={() => onCopy(copyKey, value)}
        className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800"
        aria-label={`Copy ${label.toLowerCase()}`}
      >
        {copiedItem === copyKey ? (
          <CheckIcon className="w-4 h-4 text-emerald-600" aria-hidden="true" />
        ) : (
          <ClipboardIcon className="w-4 h-4" aria-hidden="true" />
        )}
      </button>
    </div>
  </div>
);

export default DevCredentials;
